import { Button, Typography } from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";

import AppDialog from "components/common/AppDialog";
import ConfirmDialog from "components/common/ConfirmDialog";
import { useFilePreview } from "components/preview";
import { pushNotification } from "features/notificationSlice";
import {
  type SecureFile,
  useDeleteSecureFileMutation,
  useGetSecureFileAccessUrlMutation,
  useRestoreSecureFileMutation,
} from "features/secureUploads/secureUploads";
import { formatApiError } from "utils/formatApiError";

import FileDetailsDialog from "./FileDetailsDialog";
import MoveFileDialog, { type MoveMode } from "./MoveFileDialog";
import { restoreAudience, type FileAction } from "./fileModel";

/** What a file row's menu does, and the dialogs it opens. */
export function useFileActions() {
  const dispatch = useDispatch();
  const { openInlinePreview } = useFilePreview();
  const [getAccessUrl] = useGetSecureFileAccessUrlMutation();
  const [deleteFile, { isLoading: deleting }] = useDeleteSecureFileMutation();
  const [details, setDetails] = useState<SecureFile | null>(null);
  const [toDelete, setToDelete] = useState<SecureFile | null>(null);
  const [toRestore, setToRestore] = useState<SecureFile | null>(null);
  const [toMove, setToMove] = useState<{
    file: SecureFile;
    mode: MoveMode;
  } | null>(null);
  const [restoreFile, { isLoading: restoring }] =
    useRestoreSecureFileMutation();

  const toast = (message: string, severity: "success" | "error") =>
    dispatch(pushNotification({ message, severity }));

  const preview = async (file: SecureFile) => {
    try {
      const { url, file_name } = await getAccessUrl({
        id: file.id,
        mode: "preview",
      }).unwrap();
      const blob = await (await fetch(url)).blob();
      openInlinePreview(file_name, blob, { size: file.size });
    } catch {
      toast("The file could not be previewed.", "error");
    }
  };

  const download = async (file: SecureFile) => {
    try {
      const { url } = await getAccessUrl({ id: file.id }).unwrap();
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast("The file could not be downloaded.", "error");
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteFile(toDelete.id).unwrap();
      toast("File deleted.", "success");
      setToDelete(null);
    } catch (error) {
      toast(
        formatApiError(
          (error as { data?: unknown })?.data,
          "The file could not be deleted.",
        ),
        "error",
      );
    }
  };

  const confirmRestore = async () => {
    if (!toRestore) return;
    try {
      await restoreFile(toRestore.id).unwrap();
      toast(
        `File restored to ${toRestore.restore_to?.label ?? "its place"}.`,
        "success",
      );
      setToRestore(null);
    } catch (error) {
      toast(
        formatApiError(
          (error as { data?: unknown })?.data,
          "The file could not be restored.",
        ),
        "error",
      );
    }
  };

  const target = toRestore?.restore_to;

  const run = (action: FileAction, file: SecureFile) => {
    if (action === "details") setDetails(file);
    else if (action === "preview") void preview(file);
    else if (action === "download") void download(file);
    else if (action === "delete") setToDelete(file);
    else if (action === "restore") setToRestore(file);
    else if (action === "add_to_collection" || action === "move_file")
      setToMove({ file, mode: "collection" });
    else if (action === "add_to_project" || action === "move_to_project")
      setToMove({ file, mode: "project" });
  };

  const dialogs = (
    <>
      <FileDetailsDialog file={details} onClose={() => setDetails(null)} />
      <MoveFileDialog
        file={toMove?.file ?? null}
        mode={toMove?.mode ?? "collection"}
        onClose={() => setToMove(null)}
      />
      <ConfirmDialog
        open={toDelete !== null}
        title="Delete File"
        message={
          <Typography variant="body2">
            <b>{toDelete?.file_name}</b> will be removed for everyone who can
            see it. An Admin can restore it from Deleted Files.
          </Typography>
        }
        confirmLabel="Delete File"
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
        loading={deleting}
      />
      {target?.refusal ? (
        <AppDialog
          open
          onClose={() => setToRestore(null)}
          title="Restore File"
          maxWidth="xs"
          actions={
            <Button variant="outlined" onClick={() => setToRestore(null)}>
              Close
            </Button>
          }
        >
          <Typography variant="body2">
            <b>{toRestore?.file_name}</b> belongs to a closed project, where
            nothing changes. {target.refusal}
          </Typography>
        </AppDialog>
      ) : (
        <ConfirmDialog
          open={toRestore !== null}
          title="Restore File"
          message={
            <>
              <Typography variant="body2">
                <b>{toRestore?.file_name}</b> goes back to{" "}
                <b>{target?.label}</b>.
              </Typography>
              {toRestore && restoreAudience(toRestore) ? (
                <Typography variant="body2" sx={{ mt: 1.5 }}>
                  {restoreAudience(toRestore)}
                </Typography>
              ) : null}
            </>
          }
          confirmLabel="Restore"
          onConfirm={confirmRestore}
          onCancel={() => setToRestore(null)}
          loading={restoring}
        />
      )}
    </>
  );

  return { run, dialogs };
}
